import React from 'react';
import { SlideLayout } from '../ui/SlideLayout';
import { LinkIcon, Link2Icon, CornerDownRightIcon, FileTextIcon, Heading1Icon, ImageOffIcon, CodeIcon, FileCodeIcon, FileCode2Icon, PaletteIcon, ArrowRightLeftIcon, ExternalLinkIcon, LinkIcon as Link3Icon } from 'lucide-react';
type CircleProgressProps = {
  percentage: number;
  label: string;
  colorClass: string;
};
const CircleProgress: React.FC<CircleProgressProps> = ({
  percentage,
  label,
  colorClass
}) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - percentage / 100 * circumference;
  return <div className={colorClass} data-id="element-27">
      <div className="flex flex-col items-center glass-card p-3 sm:p-6" data-id="element-28">
        <div className="relative w-16 h-16 sm:w-24 sm:h-24 flex items-center justify-center mb-2 sm:mb-4" data-id="element-29">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96" data-id="element-30">
            <circle cx="48" cy="48" r={radius} className="stroke-slate-200 fill-none" strokeWidth="8" data-id="element-31" />
            <circle cx="48" cy="48" r={radius} className={`fill-none ${colorClass} transition-all duration-1000 ease-out`} strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" data-id="element-32" />
          </svg>
          <span className="absolute text-lg sm:text-2xl font-heading font-bold text-slate-900" data-id="element-33">
            {percentage}
          </span>
        </div>
        <span className="text-[10px] sm:text-sm font-semibold text-slate-600 text-center uppercase tracking-wider" data-id="element-34">
          {label}
        </span>
      </div>
    </div>;
};
const IssueItem = ({
  icon: Icon,
  label
}: {
  icon: any;
  label: string;
}) => <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-slate-50 border border-slate-100" data-id="element-35">
    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-md bg-red-500/10 flex items-center justify-center shrink-0" data-id="element-36">
      <Icon className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" data-id="element-37" />
    </div>
    <span className="text-xs sm:text-sm text-slate-700 font-medium" data-id="element-38">
      {label}
    </span>
    <div className="ml-auto w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)] shrink-0" data-id="element-39" />
  </div>;
const IssueSectionHeader = ({
  title
}: {
  title: string;
}) => <div className="col-span-1 sm:col-span-2 lg:col-span-3 mt-2 first:mt-0" data-id="element-40">
    <h4 className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-41">
      {title}
    </h4>
  </div>;
export function AuditScorecard() {
  return <>
      <SlideLayout data-id="element-42">
        <div className="mb-6 sm:mb-10" data-id="element-43">
          <div className="w-12 h-1 bg-gold-500 mb-4 sm:mb-6 rounded-full" data-id="element-44" />
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-45">
            Website Audit Scorecard
          </h2>
          <p className="text-sm sm:text-xl text-slate-500" data-id="element-46">
            Current technical performance and identified SEO roadblocks.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-12" data-id="element-47">
          <CircleProgress percentage={87} label="Desktop Speed" colorClass="stroke-green-500" data-id="element-48" />
          <CircleProgress percentage={61} label="Mobile Speed" colorClass="stroke-orange-500" data-id="element-49" />
          <CircleProgress percentage={8} label="Domain Authority" colorClass="stroke-red-500" data-id="element-50" />
          <CircleProgress percentage={81} label="Website Health" colorClass="stroke-green-500" data-id="element-51" />
        </div>

        <div className="glass-card p-4 sm:p-8" data-id="element-52">
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2" data-id="element-53">
            <span className="w-2 h-5 sm:h-6 bg-red-500 rounded-full inline-block" data-id="element-54" />
            Technical Issues Identified
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4" data-id="element-55">
            <IssueSectionHeader title="HTTP Issues" data-id="element-56" />
            <IssueItem icon={CornerDownRightIcon} label="3XX HTTP Status Codes" data-id="element-57" />
            <IssueItem icon={FileTextIcon} label="Missing Descriptions" data-id="element-58" />
            <IssueItem icon={Heading1Icon} label="H1 Tags Missing" data-id="element-59" />
            <IssueItem icon={Heading1Icon} label="Multiple H1 Tags" data-id="element-60" />
            <IssueItem icon={ImageOffIcon} label="Alt Text Missing" data-id="element-61" />
            <IssueItem icon={FileCodeIcon} label="JavaScript Not Minified" data-id="element-62" />
            <IssueItem icon={FileCode2Icon} label="Too Many JavaScript Files" data-id="element-63" />
            <IssueItem icon={PaletteIcon} label="External CSS 3XX/4XX/5XX" data-id="element-64" />
            <IssueItem icon={CodeIcon} label="CSS Not Compressed/Minified" data-id="element-65" />

            <IssueSectionHeader title="Linking Issues" data-id="element-66" />
            <IssueItem icon={LinkIcon} label="No Inbound Links" data-id="element-67" />
            <IssueItem icon={Link2Icon} label="One Inbound Internal Link" data-id="element-68" />
            <IssueItem icon={ArrowRightLeftIcon} label="Internal Links to 3XX Redirects" data-id="element-69" />
            <IssueItem icon={ExternalLinkIcon} label="External Links to 3XX/4XX or Missing Anchor" data-id="element-70" />
            <IssueItem icon={LinkIcon} label="Nofollow External Links" data-id="element-71" />
          </div>
        </div>
      </SlideLayout>
    </>;
}